/**
 * The phone layout's per-location chart cache: one entry per user, pour and
 * location, in memory only (site phones may be shared), at most 60 entries
 * (least recently used dropped first), cleared on logout through
 * mobileSessionCaches.
 *
 * What a load asks for (getLocationChartSeries):
 *  - The first load of temp, tank and diff is windowed: since = the
 *    location's newest reading (else now) minus the selected range, so a
 *    24h chart never downloads two weeks of readings and a completed pour
 *    still shows its last day. 'All' loads the whole history; 'ea' always
 *    does (the strength and maturity charts mark targets along the whole
 *    curve), and so does 'eva' (about 48 points a day).
 *  - Later loads are deltas: since = the newest point held. 'eva' is always
 *    asked for alone, and with a since only from its own newest point: the
 *    server's 30-minute buckets carry on from it, matching a full
 *    calculation; any other since would shift every bucket. Without an
 *    evaporation point held it loads in full. temp, tank, ea and diff share
 *    one call from the earliest of their newest points (chartDelta drops the
 *    repeats). A series over an hour behind the others asks on its own, and
 *    the differentials' cursor leaves out a partner over an hour behind the
 *    newest (the server leaves a partner with no new points out of the
 *    reply), so a silent tank or partner never drags every poll back.
 *  - A series reloads from scratch when the location's concrete start
 *    (ea_start in the dashboard summary) changed, when its last scratch load
 *    is over 60 minutes old (a windowed series then reloads the selected
 *    range's window, so the window moves on), when a wider range needs older
 *    readings, or on `force`.
 *  - A completed pour is not polled again once loaded after it completed.
 *  - An older backend that takes only 'all' / 'ea' (the service remembers
 *    its 422): one full call per load, and its 'all' reply fills every
 *    series (the differentials only when they were asked for).
 *  - Identical calls in flight are shared, at most 3 calls run at once, and
 *    a quiet delta poll gives up after 20 s. A failed load keeps what is
 *    held.
 */
import {
  chartSeriesListSupported,
  poursAPI,
  type ChartDataResponse,
  type ChartSeriesKey,
  type LocationStatus,
} from '../../../services/pours';
import { parseUtc, type IsoLike } from '../../../utils/datetime';
import { registerMobileCacheClear } from '../../../utils/mobileSessionCaches';
import { pourStatusEnded } from '../../../utils/onlineStatus';
import { ALL_SERIES, diffCursor, mergeChartReply, newestBySeries } from './chartDelta';

/** Most entries kept (user, pour, location). */
export const MAX_CHART_ENTRIES = 60;
/** Most chart calls running at once. */
export const MAX_CHARTS_IN_FLIGHT = 3;
/** A quiet delta poll is abandoned after this long. */
export const POLL_TIMEOUT_MS = 20 * 1000;
/** A series older than this (since its last scratch load) reloads in full. */
export const FULL_RELOAD_MS = 60 * 60 * 1000;
/** Data younger than this needs no reload when a chart mounts. */
export const CHART_FRESH_MS = 60 * 1000;
/** A delta cursor further than this behind the newest one of its call (a
 *  tank or a differential partner gone silent) does not pull the others
 *  back: the series gets a call of its own, and a silent partner is left
 *  out of the differentials' cursor (diffCursor). */
export const CURSOR_LAG_MS = 60 * 60 * 1000;

/** Series whose first load is windowed by the selected range. Never 'eva':
 *  its since must be an evaporation point held (see the module comment). */
const WINDOWED = new Set<ChartSeriesKey>(['temp', 'tank', 'diff']);

interface SeriesState {
  /** null: the whole history is held; a number: only points after it. */
  from: number | null;
  /** The last scratch (not delta) load. */
  baseAt: number;
  /** The last successful call that carried this series. */
  fetchedAt: number;
  /** Loaded while the pour was complete: final, never polled again. */
  final: boolean;
  /** Must reload from scratch: the concrete start changed. */
  stale: boolean;
}

export interface ChartCacheEntry {
  /** The merged chart data; null until the first reply. */
  data: ChartDataResponse | null;
  series: Partial<Record<ChartSeriesKey, SeriesState>>;
  /** The location's concrete start when the series were loaded. */
  eaStart: string | null;
  /** Bumped when the entry is reset: replies to older calls are dropped. */
  gen: number;
}

const entries = new Map<string, ChartCacheEntry>();
let genCounter = 0;

type Listener = (key: string | null) => void;
const listeners = new Set<Listener>();

/** Tell the hooks an entry changed (null: every entry). */
const notify = (key: string | null): void => {
  listeners.forEach((listener) => {
    try {
      listener(key);
    } catch (error) {
      console.error('Chart cache listener failed:', error);
    }
  });
};

/** The cache key of a location's chart: user, pour and location. */
export const chartCacheKey = (
  userId: string | null | undefined,
  pourId: string,
  locationId: string,
): string => `${userId ?? ''}|${pourId}|${locationId}`;

/** Listen for entry changes (the key, or null when the cache was cleared);
 *  returns the unsubscribe function. */
export const subscribeChartCache = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The entry for a key, without counting as a use. */
export const peekChartEntry = (key: string): ChartCacheEntry | null => entries.get(key) ?? null;

/** Whether every series of `need` has been loaded for the key. */
export const hasChartSeries = (key: string, need: ReadonlyArray<ChartSeriesKey>): boolean => {
  const entry = entries.get(key);
  return !!entry?.data && need.every((s) => entry.series[s] !== undefined);
};

export const clearChartCache = (): void => {
  entries.clear();
  notify(null);
};

registerMobileCacheClear(clearChartCache);

/** Most recently used moves to the end; the oldest past the limit go. */
const touch = (key: string, entry: ChartCacheEntry): void => {
  entries.delete(key);
  entries.set(key, entry);
  while (entries.size > MAX_CHART_ENTRIES) {
    const oldest = entries.keys().next();
    if (oldest.done) break;
    entries.delete(oldest.value);
  }
};

// ---------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------

/** One chart call. */
export interface PlannedChartCall {
  series: ChartSeriesKey[];
  /** Epoch ms, exclusive; null loads the whole history. */
  since: number | null;
  /** Not a delta of what is held: the reply replaces the series. */
  replace: boolean;
}

export interface PlanOptions {
  /** The selected range's length; null for All. */
  rangeMs: number | null;
  now: number;
  /** Where the range window ends: the location's newest reading (so a
   *  completed or long-silent location still gets its last 24 h), else
   *  now. */
  anchor?: number | null;
  /** The pour is complete (status '2'). */
  ended: boolean;
  force?: boolean;
}

/** Where a windowed series must start for the range: the range back from
 *  the anchor; null (the whole history) for All or a series never
 *  windowed. */
const windowFrom = (s: ChartSeriesKey, rangeMs: number | null, anchor: number): number | null =>
  WINDOWED.has(s) && rangeMs !== null ? anchor - rangeMs : null;

/** The window's end: the location's newest reading when known (never
 *  ahead of now), else now. */
const windowAnchor = (lastRead: IsoLike, now: number): number => {
  const read = parseUtc(lastRead).getTime();
  return Number.isNaN(read) ? now : Math.min(read, now);
};

/** Whether held points from `held` cover a chart from `wanted`. */
const covers = (held: number | null, wanted: number | null): boolean =>
  held === null || (wanted !== null && held <= wanted);

/** The wider of two starts (null, the whole history, is widest). */
const widest = (a: number | null, b: number | null): number | null =>
  a === null || b === null ? null : Math.min(a, b);

/** `need` in the call's order, without repeats. */
const ordered = (need: ReadonlyArray<ChartSeriesKey>): ChartSeriesKey[] =>
  ALL_SERIES.filter((s) => need.includes(s));

/**
 * The calls that bring `need` up to date for the range (see the module
 * comment). Empty when nothing needs loading (a completed pour already
 * loaded after it completed).
 */
export const planChartCalls = (
  entry: ChartCacheEntry | null | undefined,
  need: ReadonlyArray<ChartSeriesKey>,
  { rangeMs, now, anchor, ended, force = false }: PlanOptions,
): PlannedChartCall[] => {
  const end = anchor ?? now;
  const newest = newestBySeries(entry?.data);
  const scratch: { series: ChartSeriesKey; since: number | null }[] = [];
  // A delta's cursor is the newest point held. An empty series has none and
  // rides along from the other series' cursor (a location without a tank
  // must not pull the call back to the window start); `floor` is where its
  // held points start (null: the whole history).
  const deltas: { series: ChartSeriesKey; cursor: number | null; floor: number | null }[] = [];

  ordered(need).forEach((s) => {
    const state = entry?.data ? entry.series[s] : undefined;
    const wanted = windowFrom(s, rangeMs, end);
    if (!state) {
      scratch.push({ series: s, since: wanted });
    } else if (force || state.stale) {
      scratch.push({ series: s, since: widest(state.from, wanted) });
    } else if (!covers(state.from, wanted)) {
      scratch.push({ series: s, since: wanted });
    } else if (ended && state.final) {
      // Complete and loaded since: nothing more will arrive.
    } else if (!ended && now - state.baseAt > FULL_RELOAD_MS) {
      // The selected range's window from the newest reading (null: the
      // whole history), so a long session does not keep every hour since
      // the first load.
      scratch.push({ series: s, since: wanted });
    } else {
      const cursor = s === 'diff' ? diffCursor(entry?.data?.difftemp, CURSOR_LAG_MS) : newest[s];
      deltas.push({ series: s, cursor, floor: state.from });
    }
  });

  if (scratch.length === 0 && deltas.length === 0) return [];

  // An older backend: one full call with everything needed.
  if (!chartSeriesListSupported()) {
    return [{ series: ordered([...scratch, ...deltas].map((c) => c.series)), since: null, replace: true }];
  }

  /** A delta call from the earliest cursor. With no point held in any of
   *  its series: from the earliest window start when every series is
   *  windowed, else a full call (the lists are empty or new). */
  const deltaCall = (group: { series: ChartSeriesKey; cursor: number | null; floor: number | null }[]): PlannedChartCall => {
    const series = group.map((c) => c.series);
    const cursors = group.map((c) => c.cursor).filter((c): c is number => c !== null);
    if (cursors.length > 0) return { series, since: Math.min(...cursors), replace: false };
    const floors = group.map((c) => c.floor);
    return floors.every((f): f is number => f !== null)
      ? { series, since: Math.min(...floors), replace: false }
      : { series, since: null, replace: true };
  };

  const calls: PlannedChartCall[] = [];
  // Evaporation alone: its since anchors the server's 30-minute buckets, so
  // it is only ever the newest evaporation point held; else a full load.
  const evaScratch = scratch.find((c) => c.series === 'eva');
  const evaDelta = deltas.find((c) => c.series === 'eva');
  if (evaScratch) calls.push({ series: ['eva'], since: null, replace: true });
  if (evaDelta) {
    calls.push(
      evaDelta.cursor !== null
        ? { series: ['eva'], since: evaDelta.cursor, replace: false }
        : { series: ['eva'], since: null, replace: true },
    );
  }

  // Scratch loads that share a start share a call.
  const bySince = new Map<string, { since: number | null; series: ChartSeriesKey[] }>();
  scratch
    .filter((c) => c.series !== 'eva')
    .forEach((c) => {
      const key = String(c.since);
      const group = bySince.get(key) ?? { since: c.since, series: [] };
      group.series.push(c.series);
      bySince.set(key, group);
    });
  bySince.forEach((group) => {
    calls.push({ series: group.series, since: group.since, replace: true });
  });

  // The other deltas share a call, except a series whose newest point is
  // far behind the rest (a silent tank): it asks on its own.
  const rest = deltas.filter((c) => c.series !== 'eva');
  const cursors = rest.map((c) => c.cursor).filter((c): c is number => c !== null);
  const lead = cursors.length > 0 ? Math.max(...cursors) : null;
  const lagging = (c: { cursor: number | null }): boolean =>
    lead !== null && c.cursor !== null && c.cursor < lead - CURSOR_LAG_MS;
  const shared = rest.filter((c) => !lagging(c));
  if (shared.length > 0) calls.push(deltaCall(shared));
  rest.filter(lagging).forEach((c) => calls.push(deltaCall([c])));
  return calls;
};

// ---------------------------------------------------------------------------
// Calls: shared when identical, at most 3 at once
// ---------------------------------------------------------------------------

let active = 0;
const waiting: Array<() => void> = [];

const acquire = (): Promise<void> => {
  if (active < MAX_CHARTS_IN_FLIGHT) {
    active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiting.push(() => {
      active += 1;
      resolve();
    });
  });
};

const release = (): void => {
  active = Math.max(0, active - 1);
  const next = waiting.shift();
  if (next) next();
};

const inFlight = new Map<string, Promise<void>>();

interface CallContext {
  key: string;
  pourId: string;
  locationId: string;
  gen: number;
  ended: boolean;
  now: number;
  quiet: boolean;
}

/** Merge a reply into the entry it was asked for (dropped when the entry
 *  was reset or cleared meanwhile). */
const applyReply = (ctx: CallContext, call: PlannedChartCall, reply: ChartDataResponse): void => {
  const entry = entries.get(ctx.key);
  if (!entry || entry.gen !== ctx.gen) return;
  // An older backend answered 'all' (or 'ea'): full lists of everything,
  // the differentials counted only when asked for (a fallback that skips
  // them server-side would send none).
  const legacy = !chartSeriesListSupported();
  const eaOnly = call.series.length === 1 && call.series[0] === 'ea';
  const requested = legacy && !eaOnly
    ? ALL_SERIES.filter((s) => s !== 'diff' || call.series.includes('diff'))
    : call.series;
  const full = legacy || call.replace || reply.since_applied == null;
  entry.data = mergeChartReply(entry.data, reply, requested, { replace: full });
  const from = legacy || reply.since_applied == null ? null : reply.since_applied;
  requested.forEach((s) => {
    const held = entry.series[s];
    entry.series[s] = full || !held
      ? { from, baseAt: ctx.now, fetchedAt: ctx.now, final: ctx.ended, stale: false }
      : { ...held, fetchedAt: ctx.now, final: ctx.ended };
  });
  notify(ctx.key);
};

/** Run one call (shared with an identical call in flight). */
const runCall = (ctx: CallContext, call: PlannedChartCall): Promise<void> => {
  const id = `${ctx.key}|${ctx.gen}|${call.series.join(',')}|${call.since ?? 'all'}|${call.replace ? 'r' : 'd'}`;
  const running = inFlight.get(id);
  if (running) return running;
  const promise = (async () => {
    await acquire();
    // Only a quiet delta poll gives up: a first load may be slow on site.
    const controller = ctx.quiet && !call.replace && typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;
    const timer = controller ? setTimeout(() => controller.abort(), POLL_TIMEOUT_MS) : null;
    try {
      const reply = await poursAPI.getLocationChartSeries(ctx.pourId, ctx.locationId, {
        series: call.series,
        since: call.since,
        ...(controller ? { signal: controller.signal } : {}),
      });
      applyReply(ctx, call, reply);
    } finally {
      if (timer) clearTimeout(timer);
      release();
      inFlight.delete(id);
    }
  })();
  inFlight.set(id, promise);
  return promise;
};

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export interface LoadLocationChartArgs {
  userId: string | null | undefined;
  pourId: string;
  /** The dashboard summary row: its ea_start says when the concrete start
   *  changed, its last_read where the range window ends. */
  location: Pick<LocationStatus, 'id' | 'ea_start'> & { last_read?: IsoLike };
  need: ReadonlyArray<ChartSeriesKey>;
  /** The pour's status code ('2' complete). */
  pourStatus?: string | null;
  /** The selected range's length; null for All. */
  rangeMs: number | null;
  /** A refresh poll: a delta gives up after 20 s. */
  quiet?: boolean;
  /** Reload the needed series from scratch. */
  force?: boolean;
  /** Tests: the time to plan from. */
  now?: number;
}

/** The entry for the args, created or reset (concrete start changed) as
 *  needed, and counted as used. */
const entryFor = (key: string, eaStart: string | null | undefined): ChartCacheEntry => {
  let entry = entries.get(key);
  if (!entry) {
    genCounter += 1;
    entry = { data: null, series: {}, eaStart: eaStart ?? null, gen: genCounter };
  } else if (eaStart !== undefined && (eaStart ?? null) !== entry.eaStart) {
    // The concrete start changed (here or on another device): maturity was
    // recalculated, so everything held reloads; the data stays on screen
    // until it does, and replies to older calls are dropped.
    genCounter += 1;
    entry.gen = genCounter;
    entry.eaStart = eaStart ?? null;
    Object.values(entry.series).forEach((state) => {
      if (state) state.stale = true;
    });
  }
  touch(key, entry);
  return entry;
};

/**
 * Bring a location's chart data up to date for `need` and the range (see
 * the module comment) and return it. Rejects when a call failed; the data
 * held is kept and still returned by later reads.
 */
export const loadLocationChart = async ({
  userId,
  pourId,
  location,
  need,
  pourStatus,
  rangeMs,
  quiet = false,
  force = false,
  now = Date.now(),
}: LoadLocationChartArgs): Promise<ChartDataResponse | null> => {
  const key = chartCacheKey(userId, pourId, location.id);
  const entry = entryFor(key, location.ea_start);
  const ended = pourStatusEnded(pourStatus);
  const anchor = windowAnchor(location.last_read, now);
  const calls = planChartCalls(entry, need, { rangeMs, now, anchor, ended, force });
  if (calls.length > 0) {
    const ctx: CallContext = { key, pourId, locationId: location.id, gen: entry.gen, ended, now, quiet };
    const results = await Promise.allSettled(calls.map((call) => runCall(ctx, call)));
    const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed) throw failed.reason;
  }
  return entries.get(key)?.data ?? null;
};

/** What a chart mounting on cached data should do: 'missing' (load, with a
 *  skeleton when nothing is held), 'stale' (refresh quietly) or 'fresh'
 *  (nothing). */
export type ChartEntryStatus = 'missing' | 'stale' | 'fresh';

export const chartEntryStatus = ({
  userId,
  pourId,
  location,
  need,
  pourStatus,
  rangeMs,
  now = Date.now(),
}: Omit<LoadLocationChartArgs, 'quiet' | 'force'>): ChartEntryStatus => {
  const entry = entries.get(chartCacheKey(userId, pourId, location.id));
  if (!entry?.data) return 'missing';
  const anchor = windowAnchor(location.last_read, now);
  const series = ordered(need);
  const states = series.map((s) => entry.series[s]);
  if (states.some((state, i) => !state || !covers(state.from, windowFrom(series[i], rangeMs, anchor)))) {
    return 'missing';
  }
  const eaStart = location.ea_start;
  if (eaStart !== undefined && (eaStart ?? null) !== entry.eaStart) return 'stale';
  if (states.some((state) => state?.stale)) return 'stale';
  if (pourStatusEnded(pourStatus) && states.every((state) => state?.final)) return 'fresh';
  return states.some((state) => !state || now - state.fetchedAt > CHART_FRESH_MS) ? 'stale' : 'fresh';
};

/** Tests only: forget every entry and call. */
export const resetChartCacheForTests = (): void => {
  entries.clear();
  inFlight.clear();
  waiting.length = 0;
  active = 0;
  listeners.clear();
};
